<template>
  <HrPageLayout
    title="Records Dashboard"
    subtitle="Statistics and graphs for Employee Records with filtering"
    icon="📊"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Employee Records Management' },
      { label: 'Records Dashboard' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="loadDashboard">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="goEmployeeRecords">
        <i class="mdi mdi-folder-account"></i> Employee Records
      </button>
    </template>

    <div class="erd-wrap">
      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-filter-alt"></i> Filters</h5>
        </div>
        <div class="hr-chart-body">
          <div class="erd-filters">
            <label class="erd-field">
              <span>Category / status</span>
              <select v-model="filters.status" class="hr-select" @change="loadDashboard">
                <option value="">All categories</option>
                <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </label>
            <label class="erd-field">
              <span>Department</span>
              <select v-model="filters.departmentId" class="hr-select" @change="loadDashboard">
                <option value="">All departments</option>
                <option v-for="d in departments" :key="'d-' + d.id" :value="String(d.id)">{{ d.departmentName }}</option>
              </select>
            </label>
            <label class="erd-field">
              <span>Date from</span>
              <input v-model="filters.dateFrom" type="date" class="hr-input" @change="loadDashboard">
            </label>
            <label class="erd-field">
              <span>Date to</span>
              <input v-model="filters.dateTo" type="date" class="hr-input" @change="loadDashboard">
            </label>
            <label class="erd-field">
              <span>Attachments</span>
              <select v-model="filters.hasFile" class="hr-select" @change="loadDashboard">
                <option value="">All</option>
                <option value="yes">With files</option>
                <option value="no">Without files</option>
              </select>
            </label>
            <div class="erd-field erd-field-actions">
              <button type="button" class="hr-btn hr-btn-outline-secondary" @click="resetFilters">Clear filters</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loadError" class="hr-alert hr-alert-danger hr-mb-3">
        {{ loadError }}
        <button type="button" class="hr-btn hr-btn-sm hr-mt-2" @click="loadDashboard">Retry</button>
      </div>

      <div v-if="loading && !stats.total && !loadError" class="hr-loading-wrap">
        <div class="hr-spinner"></div>
        <p class="hr-text-muted">Loading records statistics…</p>
      </div>

      <template v-else>
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-folder"></i></div>
            <p class="hr-metric-label">Total Records</p>
            <h2 class="hr-metric-value">{{ stats.total || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
            <p class="hr-metric-label">Present Active</p>
            <h2 class="hr-metric-value">{{ stats.present_active || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
            <p class="hr-metric-label">Missing Active</p>
            <h2 class="hr-metric-value">{{ stats.missing_active || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-pause-circle"></i></div>
            <p class="hr-metric-label">Stoppage</p>
            <h2 class="hr-metric-value">{{ stats.stoppage || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-transfer"></i></div>
            <p class="hr-metric-label">Transferred out</p>
            <h2 class="hr-metric-value">{{ stats.transferred_out || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-user-x"></i></div>
            <p class="hr-metric-label">Attrition</p>
            <h2 class="hr-metric-value">{{ stats.attrition || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
            <p class="hr-metric-label">With files</p>
            <h2 class="hr-metric-value">{{ stats.with_files || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-file-blank"></i></div>
            <p class="hr-metric-label">Without files</p>
            <h2 class="hr-metric-value">{{ stats.without_files || 0 }}</h2>
          </div>
        </div>

        <div class="erd-charts">
          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt-2"></i> Records by category</h5>
            </div>
            <div class="hr-chart-body">
              <div class="erd-canvas-wrap">
                <canvas ref="statusChartCanvas"></canvas>
              </div>
              <p v-if="!hasStatusData" class="hr-text-muted hr-text-center hr-mt-2">No data for this filter.</p>
            </div>
          </div>

          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-bar-chart-alt-2"></i> Top departments</h5>
            </div>
            <div class="hr-chart-body">
              <div class="erd-canvas-wrap">
                <canvas ref="deptChartCanvas"></canvas>
              </div>
              <p v-if="!byDepartment.length" class="hr-text-muted hr-text-center hr-mt-2">No department breakdown available.</p>
            </div>
          </div>

          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-donate-heart"></i> Attrition breakdown</h5>
            </div>
            <div class="hr-chart-body">
              <div class="erd-canvas-wrap">
                <canvas ref="attritionChartCanvas"></canvas>
              </div>
              <p v-if="!hasAttritionData" class="hr-text-muted hr-text-center hr-mt-2">No attrition records in this filter.</p>
            </div>
          </div>

          <div class="hr-chart-card">
            <div class="hr-chart-header">
              <h5 class="hr-chart-title"><i class="bx bx-line-chart"></i> Records over time</h5>
            </div>
            <div class="hr-chart-body">
              <div class="erd-canvas-wrap">
                <canvas ref="monthChartCanvas"></canvas>
              </div>
              <p v-if="!byMonth.length" class="hr-text-muted hr-text-center hr-mt-2">No time-series data available.</p>
            </div>
          </div>
        </div>

        <p v-if="generatedAt" class="hr-text-muted hr-small hr-mt-3">Last updated: {{ generatedAt }}</p>
      </template>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";
import Chart from "chart.js/auto";

const STATUS_OPTIONS = [
  { value: "present_active", label: "Present Active" },
  { value: "missing_active", label: "Missing Active files" },
  { value: "stoppage", label: "Stoppage" },
  { value: "transferred_out", label: "Transferred out" },
  { value: "attrition", label: "Attrition" }
];

const COLORS = ["#0f766e", "#0891b2", "#d97706", "#7c3aed", "#dc2626", "#64748b", "#059669", "#2563eb"];

export default {
  name: "RecordsDashboard",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      loadError: "",
      departments: [],
      statusOptions: STATUS_OPTIONS,
      filters: {
        status: "",
        departmentId: "",
        dateFrom: "",
        dateTo: "",
        hasFile: ""
      },
      stats: {},
      statusChart: [],
      attritionChart: [],
      byDepartment: [],
      byMonth: [],
      generatedAt: "",
      charts: {
        status: null,
        dept: null,
        attrition: null,
        month: null
      }
    };
  },
  computed: {
    hasStatusData() {
      return (this.statusChart || []).some((r) => Number(r.count || 0) > 0);
    },
    hasAttritionData() {
      return (this.attritionChart || []).some((r) => Number(r.count || 0) > 0);
    }
  },
  mounted() {
    this.loadDepartments();
    this.loadDashboard();
  },
  beforeUnmount() {
    this.destroyCharts();
  },
  methods: {
    goEmployeeRecords() {
      this.$router.push({ name: "file-registry" });
    },
    resetFilters() {
      this.filters = {
        status: "",
        departmentId: "",
        dateFrom: "",
        dateTo: "",
        hasFile: ""
      };
      this.loadDashboard();
    },
    destroyCharts() {
      Object.keys(this.charts).forEach((k) => {
        if (this.charts[k]) {
          this.charts[k].destroy();
          this.charts[k] = null;
        }
      });
    },
    async loadDepartments() {
      try {
        const fd = new FormData();
        fd.append("function", "getDepartments");
        const res = await execute(fd, authUrl);
        const payload = res?.data?.data || res?.data || {};
        let list = payload.departments || payload.Records || payload || [];
        if (!Array.isArray(list)) list = [];
        this.departments = list
          .map((d) => ({
            id: d.id ?? d.departmentID ?? d.departmentId,
            departmentName: d.departmentName || d.name || "—"
          }))
          .filter((d) => d.id != null);
      } catch (e) {
        this.departments = [];
      }
    },
    async loadDashboard() {
      this.loading = true;
      this.loadError = "";
      try {
        const fd = new FormData();
        fd.append("function", "getEmployeeRecordsDashboard");
        if (this.filters.status) fd.append("status", this.filters.status);
        if (this.filters.departmentId) fd.append("departmentId", this.filters.departmentId);
        if (this.filters.dateFrom) fd.append("dateFrom", this.filters.dateFrom);
        if (this.filters.dateTo) fd.append("dateTo", this.filters.dateTo);
        if (this.filters.hasFile) fd.append("hasFile", this.filters.hasFile);
        const res = await execute(fd, authUrl);
        const body = res?.data || {};
        if (body.success === false) {
          this.loadError = body.message || "Failed to load Records Dashboard.";
          return;
        }
        const payload = body.data || {};
        this.stats = payload.stats || {};
        this.statusChart = payload.statusChart || [];
        this.attritionChart = payload.attritionChart || [];
        this.byDepartment = payload.byDepartment || [];
        this.byMonth = payload.byMonth || [];
        this.generatedAt = payload.generatedAt || "";
        this.$nextTick(() => this.renderCharts());
      } catch (e) {
        this.loadError = e?.message || "Failed to load Records Dashboard.";
      } finally {
        this.loading = false;
      }
    },
    renderCharts() {
      this.destroyCharts();
      this.renderStatusChart();
      this.renderDeptChart();
      this.renderAttritionChart();
      this.renderMonthChart();
    },
    renderStatusChart() {
      const el = this.$refs.statusChartCanvas;
      if (!el || !this.hasStatusData) return;
      const labels = this.statusChart.map((r) => r.label);
      const data = this.statusChart.map((r) => Number(r.count || 0));
      this.charts.status = new Chart(el, {
        type: "doughnut",
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: COLORS.slice(0, labels.length),
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } }
        }
      });
    },
    renderDeptChart() {
      const el = this.$refs.deptChartCanvas;
      if (!el || !this.byDepartment.length) return;
      const labels = this.byDepartment.map((r) => r.department);
      const data = this.byDepartment.map((r) => Number(r.count || 0));
      this.charts.dept = new Chart(el, {
        type: "bar",
        data: {
          labels,
          datasets: [{
            label: "Records",
            data,
            backgroundColor: "#0f766e"
          }]
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { x: { beginAtZero: true, ticks: { precision: 0 } } }
        }
      });
    },
    renderAttritionChart() {
      const el = this.$refs.attritionChartCanvas;
      if (!el || !this.hasAttritionData) return;
      const labels = this.attritionChart.map((r) => r.label);
      const data = this.attritionChart.map((r) => Number(r.count || 0));
      this.charts.attrition = new Chart(el, {
        type: "pie",
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: COLORS.slice(0, labels.length)
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } }
        }
      });
    },
    renderMonthChart() {
      const el = this.$refs.monthChartCanvas;
      if (!el || !this.byMonth.length) return;
      const labels = this.byMonth.map((r) => r.month);
      const data = this.byMonth.map((r) => Number(r.count || 0));
      this.charts.month = new Chart(el, {
        type: "line",
        data: {
          labels,
          datasets: [{
            label: "Records",
            data,
            borderColor: "#0891b2",
            backgroundColor: "rgba(8, 145, 178, 0.15)",
            fill: true,
            tension: 0.25
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
        }
      });
    }
  }
};
</script>

<style scoped>
.erd-wrap { padding: 0; }
.erd-filters {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.85rem 1rem;
  align-items: end;
}
.erd-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #475569;
  font-weight: 600;
}
.erd-field-actions {
  justify-content: flex-end;
}
.erd-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}
.erd-canvas-wrap {
  position: relative;
  height: 280px;
}
@media (max-width: 992px) {
  .erd-charts { grid-template-columns: 1fr; }
}
</style>
